import { useMemo } from 'react';
import styled from 'styled-components';
import { sortBy } from 'lodash';
import {
  asDynamicDecimal,
  asQuantity,
  asQuantityWithUnit,
  unitForValue,
} from '../../utils/formatters';
import { PanelMainGroup, PanelSection } from '../SidePanel';
import ExportButtons from './export/ExportButtons';
import { CropVarietyDropdown, LabeledData } from './menu-components';
import HiddenSectionsSwitch from './HiddenSectionsSwitch';
import UnassignedStemsSwitch from './UnassignedStemsSwitch';
import {
  INVERTED_STEM_AREA_UNITS,
  LONG_AREA_UNITS,
  ROW_UNITS,
} from '../../constants/units';
import { useToggleState } from '../../utils/hooks';
import { FarmCropData } from './CropData';

const PanelSectionWithSpacing = styled(PanelSection)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FarmSummary = ({
  totals,
  sectionCount,
  hasUnassignedStems = false,
  cropData,
  usageData: { patternsByCrop, sectionsByCrop } = {},
  farmName,
  readonly,
  hiddenSectionCount = 0,
  sectionsForExport = [],
  activeSectionGroup = null,
  colors = [],
}) => {
  const hasHiddenSections = hiddenSectionCount > 0;
  const hasCropVarieties = useMemo(
    () => cropData.some((crop) => !!crop.cropSectionVarieties.length),
    [cropData],
  );
  const [varietiesIsOpen, toggleVarietiesOpen] = useToggleState();

  const {
    acreage,
    plantedAcreage,
    rowCount,
    totalRowLength,
    treeCount,
  } = totals;
  const sortedCropData = useMemo(() => sortBy(cropData, 'crop.name'), [
    cropData,
  ]);

  return (
    <PanelMainGroup>
      {(hasHiddenSections || hasUnassignedStems) && (
        <PanelSectionWithSpacing>
          {hasHiddenSections && (
            <HiddenSectionsSwitch hiddenSectionCount={hiddenSectionCount} />
          )}
          {hasUnassignedStems && <UnassignedStemsSwitch />}
        </PanelSectionWithSpacing>
      )}
      <PanelSection>
        <h4>Sections</h4>
        <LabeledData
          copyable
          label="field area"
          formatter={asDynamicDecimal}
          unitSpec={LONG_AREA_UNITS}
        >
          {acreage}
        </LabeledData>
        <LabeledData
          copyable
          label="planted area"
          formatter={asDynamicDecimal}
          unitSpec={LONG_AREA_UNITS}
        >
          {plantedAcreage}
        </LabeledData>
        <LabeledData
          copyable
          label="stems"
          unit={unitForValue('stems', 'stem')}
          formatter={asQuantity}
        >
          {treeCount}
        </LabeledData>
        <LabeledData
          copyable
          label="density"
          formatter={asQuantity}
          unitSpec={INVERTED_STEM_AREA_UNITS}
        >
          {/* Prevent division by zero */}
          {plantedAcreage ? treeCount / plantedAcreage : 0}
        </LabeledData>
        <LabeledData
          copyable
          label="qty"
          unit={unitForValue('sections', 'section')}
          formatter={asQuantity}
        >
          {sectionCount}
        </LabeledData>
      </PanelSection>
      <PanelSection>
        <h4>Rows</h4>
        <LabeledData
          copyable
          label="length"
          unitSpec={ROW_UNITS}
          formatter={asDynamicDecimal}
        >
          {totalRowLength}
        </LabeledData>
        <LabeledData
          copyable
          label="qty"
          unit={unitForValue('rows', 'row')}
          formatter={asQuantity}
        >
          {rowCount}
        </LabeledData>
      </PanelSection>
      {cropData.length > 0 && (
        <PanelSection>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="m-0">Crops</h4>
            {hasCropVarieties && (
              <CropVarietyDropdown
                isOpen={varietiesIsOpen}
                onClick={toggleVarietiesOpen}
                plural
              />
            )}
          </div>
          {sortedCropData.map(
            ({
              cropId,
              crop,
              totalRowLength: cropRowLength,
              treeCount: cropTreeCount,
              acreage: cropAcreage,
              cropSectionVarieties,
            }) => (
              <FarmCropData
                className="mb-2"
                key={cropId}
                crop={crop}
                qty={cropTreeCount}
                acreage={cropAcreage}
                patterns={patternsByCrop?.[cropId]}
                sections={sectionsByCrop?.[cropId]}
                showVarieties={varietiesIsOpen}
                rowLength={cropRowLength}
                varieties={cropSectionVarieties}
                colors={colors}
                readonly={readonly}
              />
            ),
          )}
        </PanelSection>
      )}
      {sectionsForExport.length > 0 && (
        <PanelSection>
          <h4>Export</h4>
          <ExportButtons
            cropData={cropData}
            sections={sectionsForExport}
            className="w-100"
            size="sm"
            outline
            color="secondary"
            title={farmName}
            subtitle={activeSectionGroup?.name}
          >{`Export ${asQuantityWithUnit(
            'sections',
            'section',
          )(sectionsForExport.length)}`}</ExportButtons>
        </PanelSection>
      )}
    </PanelMainGroup>
  );
};

export default FarmSummary;
